/**
 * Lists of campaign requests: the brand's own (`/campaigns/requests`), with the requests other
 * wallets offer it to take over (D26), and, for admins, every request by status
 * (`/admin/campaign-requests`).
 */

import { ChevronRight, Megaphone } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";

import { requestNextStep } from "../../campaigns/next-step.js";
import {
  type CampaignRequest,
  pendingTransfer,
  type RequestStatus,
  useAllRequests,
  useMyRequests,
  useOfferedRequests,
} from "../../campaigns/requests.js";
import { StatusBadge } from "../../campaigns/StatusBadge.js";
import { shortHex } from "../../components/ChainLinks.js";
import { RequireAdmin } from "../../components/RequireAdmin.js";
import { RequireWallet } from "../../components/RequireWallet.js";
import {
  EmptyState,
  ErrorMessage,
  Loading,
  PageHeader,
  Section,
  styles,
} from "../../components/ui.js";
import { formatAmount, formatWindow } from "../../format.js";

/** The signed-in brand's requests. */
export function MyRequests() {
  return (
    <RequireWallet reason="Sign in with the wallet you used for your campaign requests.">
      <MyRequestsPage />
    </RequireWallet>
  );
}

function MyRequestsPage() {
  const requests = useMyRequests();
  const offered = useOfferedRequests();
  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="For brands"
        title="Your campaign requests"
        actions={
          <Link to="/campaigns/new" className={styles.primaryButton}>
            Start a campaign
          </Link>
        }
      >
        Each request with where it stands and what it needs from you next.
      </PageHeader>
      {offered.data && offered.data.length > 0 && (
        <Section
          title="Offered to this wallet"
          description="Other wallets offer you these requests. Open one to take it over, which makes this wallet the one that deposits, or to decline it."
          className="mt-8"
        >
          <ul className="divide-y divide-zinc-900/[0.07] dark:divide-white/10">
            {offered.data.map((request) => {
              const move = pendingTransfer(request);
              return (
                <li key={request.id} className="py-3 first:pt-0 last:pb-0">
                  <Link
                    to={`/campaigns/requests/${request.id}`}
                    className={`font-medium ${styles.link}`}
                  >
                    {request.brief.title}
                  </Link>
                  <p className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-400">
                    {request.brandName} · {formatAmount(request.budget, request.token)}
                    {move && (
                      <>
                        {" "}
                        · offered by{" "}
                        <span className="font-mono text-xs" title={move.from}>
                          {shortHex(move.from)}
                        </span>
                      </>
                    )}
                  </p>
                </li>
              );
            })}
          </ul>
        </Section>
      )}
      <RequestCards
        query={requests}
        empty={
          <EmptyState
            icon={Megaphone}
            title="You have not requested a campaign yet."
            action={
              <Link to="/campaigns/new" className={styles.primaryButton}>
                Start a campaign
              </Link>
            }
          >
            Describe your campaign and its budget. Morcast reviews it and sends you the deposit
            details. You only pay for verified organic reach.
          </EmptyState>
        }
        link={(id) => `/campaigns/requests/${id}`}
        nextStep
      />
    </div>
  );
}

/** Filters of the admin list, in display order: "change" lists the changes waiting for review. */
const FILTERS: { label: string; status: RequestStatus | "change" | undefined }[] = [
  { label: "Waiting for review", status: "submitted" },
  { label: "Changes to review", status: "change" },
  { label: "Accepted", status: "accepted" },
  { label: "Rejected", status: "rejected" },
  { label: "All", status: undefined },
];

/** Every request, for the admin team. */
export function AdminRequests() {
  return (
    <RequireAdmin>
      <AdminRequestsPage />
    </RequireAdmin>
  );
}

function AdminRequestsPage() {
  const [status, setStatus] = useState<RequestStatus | "change" | undefined>("submitted");
  const requests = useAllRequests(status);

  return (
    <section>
      <PageHeader
        eyebrow="Admin"
        title="Campaign requests"
        documentTitle="Campaign requests · Admin"
      >
        Review each request's terms and brief, and the changes brands ask for once a request is
        accepted. Your notes on rejections are shown to the brand.
      </PageHeader>
      <div role="tablist" aria-label="Status" className="mt-8 flex flex-wrap gap-2">
        {FILTERS.map((filter) => (
          <button
            key={filter.label}
            type="button"
            role="tab"
            aria-selected={status === filter.status}
            onClick={() => setStatus(filter.status)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${
              status === filter.status
                ? "bg-zinc-900 text-white dark:bg-white/[0.12] dark:text-white dark:ring-1 dark:ring-white/20 dark:ring-inset"
                : "text-zinc-600 ring-1 ring-zinc-300 hover:bg-zinc-50 dark:text-zinc-400 dark:ring-white/15 dark:hover:bg-white/[0.06]"
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>
      <RequestCards
        query={requests}
        empty={status === "change" ? "No change waits for review." : "No request has this status."}
        link={(id) => `/admin/campaign-requests/${id}`}
      />
    </section>
  );
}

/**
 * A list of request cards, with loading, error and empty states. For the brand, each card also
 * says what the request needs next.
 */
function RequestCards({
  query,
  empty,
  link,
  nextStep = false,
}: {
  query: {
    isPending: boolean;
    isError: boolean;
    error: Error | null;
    data?: CampaignRequest[] | undefined;
  };
  empty: ReactNode;
  link: (id: number) => string;
  nextStep?: boolean;
}) {
  if (query.isPending) {
    return (
      <div className="mt-8">
        <Loading />
      </div>
    );
  }
  if (query.isError && query.error) {
    return <ErrorMessage error={query.error} />;
  }
  if (!query.data || query.data.length === 0) {
    return (
      <div className="mt-8 text-zinc-600 dark:text-zinc-400">
        {typeof empty === "string" ? <p>{empty}</p> : empty}
      </div>
    );
  }
  return (
    <ul className="mt-8 space-y-3">
      {query.data.map((request) => (
        <li key={request.id}>
          <Link
            to={link(request.id)}
            className={`group flex items-center gap-4 ${styles.card} transition hover:ring-brand-500/50 dark:hover:bg-ink-850/90 dark:hover:ring-brand-400/40`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{request.brief.title}</span>
                <StatusBadge request={request} />
              </div>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                {request.brandName} · {platformLabel(request)} ·{" "}
                {formatAmount(request.budget, request.token)}
              </p>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                {formatWindow(request.platform, request.startAt, request.endAt)}
              </p>
              {nextStep && (
                <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
                  {nextStepText(request)}
                </p>
              )}
            </div>
            <ChevronRight
              aria-hidden
              className="size-5 shrink-0 text-zinc-400 group-hover:text-brand-600 dark:text-zinc-500 dark:group-hover:text-brand-400"
            />
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * The next step as plain text: the whole card is already a link, so links inside it are left
 * out.
 */
function nextStepText(request: CampaignRequest): ReactNode {
  if (request.status === "funded" && request.campaign?.status === "funded") {
    const now = Date.now();
    if (now < Date.parse(request.startAt)) {
      return "Funded. The campaign starts soon.";
    }
    return now < Date.parse(request.endAt)
      ? "Running. Creators are taking part."
      : "Ended. Morcast is measuring the results.";
  }
  if (request.status === "funded" && request.campaign?.status === "settled") {
    return "Settled. The result is published.";
  }
  return requestNextStep(request);
}

/** "X", "YouTube, dedicated video" or "YouTube, integration". */
export function platformLabel(request: Pick<CampaignRequest, "platform" | "format">): string {
  if (request.platform === "X") {
    return "X";
  }
  return request.format === "INTEGRATION" ? "YouTube, integration" : "YouTube, dedicated video";
}
